import { hasPermission } from '../data/roles';
import { RBAC_USE_DB_PERMISSIONS } from '../config/rbac';

type RBACUser = {
  role?: string;
  permissions?: string[];
};

const PERMISSION_ALIASES: Record<string, string[]> = {
  analytics: ['analytics', 'analytics_dashboard', 'analytics_revenue', 'analytics_inventory', 'analytics_advanced'],
  orders: ['orders', 'orders_read', 'orders_stats', 'orders_manage', 'orders_admin'],
  view_order_details: ['view_order_details', 'orders_manage', 'orders_admin'],
  menus: [
    'menus',
    'menus_read',
    'menus_manage',
    'menus_admin',
    'categories_read',
    'categories_manage',
    'categories_admin',
    'products_read',
    'products_manage',
    'products_admin',
    'promotions_read',
    'promotions_stats',
    'promotions_manage',
    'promotions_admin',
  ],
  reviews: ['reviews', 'reviews_read', 'reviews_stats', 'reviews_create', 'reviews_moderate', 'reviews_admin'],
  customers: ['customers', 'customers_read', 'customers_stats', 'customers_manage', 'customers_admin'],
  // settings_read is an API-level permission; it does NOT grant sidebar or page access.
  // Only 'settings' (page access) or 'settings_manage' (write access) count for UI checks.
  settings: ['settings', 'settings_manage'],
  users: ['users', 'users_read', 'users_manage'],
};

/**
 * Check whether a user has a given permission.
 *
 * @param exact - When true, only the literal permission key is checked (no alias
 *   expansion). Use this for sidebar visibility checks so that removing a
 *   parent key (e.g. "analytics") actually hides the menu item even when
 *   granular sub-keys (e.g. "analytics_dashboard") are still present.
 */
export function hasEffectivePermission(
  user: RBACUser | null | undefined,
  permission: string,
  exact = false,
): boolean {
  if (!user) return false;

  if ((user.role || '').toLowerCase() === 'super_admin') {
    return true;
  }

  const userPermissions = Array.isArray(user.permissions) ? user.permissions : [];
  const effectivePermissions = (!exact && PERMISSION_ALIASES[permission]) || [permission];

  // If the API supplied any permissions at all, trust them exclusively.
  // DB values always take precedence — never blend with the hardcoded role map.
  if (RBAC_USE_DB_PERMISSIONS || userPermissions.length > 0) {
    return effectivePermissions.some((perm) => userPermissions.includes(perm));
  }

  // Pure offline/demo fallback: no API permissions present at all.
  const role = (user.role || 'viewer').toLowerCase();
  return effectivePermissions.some((perm) => hasPermission(role, perm));
}
